/**
 * 広告画像生成（Ad Image）型定義
 *
 * - 1 ドキュメント = 1 つの広告ビジュアル（Firestore: adImages/{id}）
 * - 生成構造（仕様）:
 *   01 背景画像（Gemini banana 生成）
 *   02 商材画像プレイスホルダー（座標とサイズ、画像はユーザー差し替え）
 *   03 テキスト要素（位置・フォント・サイズ・色 / Claude 生成）
 *
 * - Canva/Wix 風エディタで要素を直接編集
 * - 出力: PNG / JPG をブラウザ Canvas で書き出し
 */
import type { IsoOrTimestamp } from './common';

/** ターゲット属性 */
export type AdAudience = 'btob' | 'btoc';

/** 商材タイプ */
export type AdProductType = 'product' | 'digital' | 'service';

/** 媒体（プラットフォーム） */
export type AdPlatform =
  | 'facebook'
  | 'instagram'
  | 'instagram_story'
  | 'youtube'
  | 'youtube_shorts'
  | 'line'
  | 'banner';

/** 出力種別: 静止画 or 動画用スライド複数生成 */
export type AdMediaKind = 'image' | 'video_slides';

/** SNS サイズプリセット ID */
export type AdSizePresetId =
  | 'facebook_feed_1x1'
  | 'facebook_feed_4x5'
  | 'instagram_feed_1x1'
  | 'instagram_story_9x16'
  | 'youtube_thumbnail_16x9'
  | 'youtube_shorts_9x16'
  | 'line_card_191x1'
  | 'line_square_1x1'
  | 'line_vertical_9x16'
  | 'banner_square_1x1'
  | 'banner_vertical_4x5';

/** Gemini banana API がサポートするアスペクト比 */
export type GeminiAspectRatio =
  | '1:1'
  | '2:3'
  | '3:2'
  | '3:4'
  | '4:3'
  | '4:5'
  | '5:4'
  | '9:16'
  | '16:9'
  | '21:9';

/** SNS サイズプリセット定義 */
export interface AdSizePreset {
  id: AdSizePresetId;
  label: string;
  platform: AdPlatform;
  width: number;
  height: number;
  aspectRatio: GeminiAspectRatio;
  /** 表示用の補足説明（媒体内バリエーション等） */
  note?: string;
}

/** 業界・媒体ごとの推奨サイズ（2026 標準） */
export const AD_SIZE_PRESETS: readonly AdSizePreset[] = [
  { id: 'facebook_feed_1x1',       label: 'Facebook フィード（正方形）', platform: 'facebook',         width: 1440, height: 1440, aspectRatio: '1:1', note: '推奨 1080×1080 以上' },
  { id: 'facebook_feed_4x5',       label: 'Facebook フィード（縦）',     platform: 'facebook',         width: 1440, height: 1800, aspectRatio: '4:5' },
  { id: 'instagram_feed_1x1',      label: 'Instagram フィード',          platform: 'instagram',        width: 1440, height: 1440, aspectRatio: '1:1' },
  { id: 'instagram_story_9x16',    label: 'Instagram ストーリーズ',      platform: 'instagram_story',  width: 1440, height: 2560, aspectRatio: '9:16' },
  { id: 'youtube_thumbnail_16x9',  label: 'YouTube サムネイル',          platform: 'youtube',          width: 1280, height: 720,  aspectRatio: '16:9' },
  { id: 'youtube_shorts_9x16',     label: 'YouTube Shorts',              platform: 'youtube_shorts',   width: 1080, height: 1920, aspectRatio: '9:16' },
  { id: 'line_card_191x1',         label: 'LINE 広告 カード',            platform: 'line',             width: 1200, height: 628,  aspectRatio: '21:9', note: '1.91:1 を 21:9 で近似生成' },
  { id: 'line_square_1x1',         label: 'LINE 広告 スクエア',          platform: 'line',             width: 1080, height: 1080, aspectRatio: '1:1' },
  { id: 'line_vertical_9x16',      label: 'LINE 広告 縦型',              platform: 'line',             width: 720,  height: 1280, aspectRatio: '9:16' },
  // 汎用バナー（業務マニュアル準拠 / 2026-05-11 追加）
  // 横長 1.91:1 (1200×628) は LINE カードと同サイズのため line_card_191x1 を流用
  { id: 'banner_square_1x1',       label: '汎用バナー 正方形',           platform: 'banner',           width: 1200, height: 1200, aspectRatio: '1:1', note: '業務マニュアル準拠サイズ（必須）' },
  { id: 'banner_vertical_4x5',     label: '汎用バナー 縦長',             platform: 'banner',           width: 960,  height: 1200, aspectRatio: '4:5', note: '業務マニュアル準拠サイズ' },
] as const;

export function findAdSizePreset(id: AdSizePresetId): AdSizePreset | undefined {
  return AD_SIZE_PRESETS.find((p) => p.id === id);
}

/** デザインムード（複数選択可） */
export type AdMoodKey =
  | 'exciting'        // わくわく
  | 'pop'             // ポップ
  | 'playful'         // プレイフル
  | 'vector_art'      // ベクター画像
  | 'isometric'       // アイソメトリック
  | 'futuristic'      // 未来的
  | 'minimal'         // ミニマル
  | 'luxury'          // ラグジュアリー
  | 'organic'         // オーガニック
  | 'editorial'       // エディトリアル
  | 'retro'           // レトロ
  | 'cyberpunk'       // サイバーパンク
  | 'pastel'          // パステル
  | 'monochrome'      // モノクローム
  | 'natural'         // ナチュラル
  | 'corporate'       // コーポレート
  | 'handdrawn'       // 手描き風
  | 'flat_design'     // フラットデザイン
  | 'photorealistic'  // フォトリアル
  | 'abstract';       // 抽象的

export interface AdMoodOption {
  key: AdMoodKey;
  label: string;
  englishHint: string;
}

export const AD_MOOD_OPTIONS: readonly AdMoodOption[] = [
  { key: 'exciting',       label: 'わくわく',         englishHint: 'exciting, energetic, joyful' },
  { key: 'pop',            label: 'ポップ',           englishHint: 'pop, vibrant, colorful' },
  { key: 'playful',        label: 'プレイフル',       englishHint: 'playful, whimsical, fun' },
  { key: 'vector_art',     label: 'ベクター画像',     englishHint: 'vector art, flat illustration, clean lines' },
  { key: 'isometric',      label: 'アイソメトリック', englishHint: 'isometric 3D illustration, technical perspective' },
  { key: 'futuristic',     label: '未来的',           englishHint: 'futuristic, sci-fi, high-tech' },
  { key: 'minimal',        label: 'ミニマル',         englishHint: 'minimal, clean, lots of whitespace' },
  { key: 'luxury',         label: 'ラグジュアリー',   englishHint: 'luxury, premium, sophisticated' },
  { key: 'organic',        label: 'オーガニック',     englishHint: 'organic shapes, natural curves' },
  { key: 'editorial',      label: 'エディトリアル',   englishHint: 'editorial magazine style, refined typography' },
  { key: 'retro',          label: 'レトロ',           englishHint: 'retro, vintage, nostalgic' },
  { key: 'cyberpunk',      label: 'サイバーパンク',   englishHint: 'cyberpunk, neon, dark atmosphere' },
  { key: 'pastel',         label: 'パステル',         englishHint: 'pastel colors, soft, dreamy' },
  { key: 'monochrome',     label: 'モノクローム',     englishHint: 'monochrome, black and white' },
  { key: 'natural',        label: 'ナチュラル',       englishHint: 'natural, earthy, organic materials' },
  { key: 'corporate',      label: 'コーポレート',     englishHint: 'corporate, professional, trustworthy' },
  { key: 'handdrawn',      label: '手描き風',         englishHint: 'hand-drawn, sketchy, illustrative' },
  { key: 'flat_design',    label: 'フラットデザイン', englishHint: 'flat design, simple geometric shapes' },
  { key: 'photorealistic', label: 'フォトリアル',     englishHint: 'photorealistic, hyper-detailed photography' },
  { key: 'abstract',       label: '抽象的',           englishHint: 'abstract, conceptual, artistic' },
] as const;

/** 季節感（任意、無選択可） */
export type AdSeasonKey =
  | 'spring'      // 春（桜・新緑）
  | 'summer'      // 夏（海・夕焼け）
  | 'autumn'      // 秋（紅葉・温かみ）
  | 'winter'      // 冬（雪・凛とした）
  | 'new_year'    // 正月
  | 'valentine'   // バレンタイン
  | 'christmas'   // クリスマス
  | 'rainy';      // 梅雨

export interface AdSeasonOption {
  key: AdSeasonKey;
  label: string;
  englishHint: string;
}

export const AD_SEASON_OPTIONS: readonly AdSeasonOption[] = [
  { key: 'spring',    label: '春',         englishHint: 'spring season, cherry blossoms, fresh greenery, soft pink and green palette' },
  { key: 'summer',    label: '夏',         englishHint: 'summer season, ocean, sunset, vibrant blue and orange palette' },
  { key: 'autumn',    label: '秋',         englishHint: 'autumn season, fall colors, warm orange and brown palette' },
  { key: 'winter',    label: '冬',         englishHint: 'winter season, snow, crisp cold atmosphere, white and icy blue palette' },
  { key: 'new_year',  label: '正月',       englishHint: 'Japanese new year, traditional decorations, gold and red accents' },
  { key: 'valentine', label: 'バレンタイン', englishHint: 'Valentine\'s day, romantic, pink and red palette with hearts' },
  { key: 'christmas', label: 'クリスマス', englishHint: 'Christmas, festive, red green gold palette, warm lights' },
  { key: 'rainy',     label: '梅雨',       englishHint: 'rainy season, fresh wet atmosphere, hydrangea, blue and purple palette' },
] as const;

/** 生成設定（タブから入力） */
export interface AdImageConfig {
  audience: AdAudience;
  productType: AdProductType;
  mediaKind: AdMediaKind;
  platform: AdPlatform;
  sizePresetId: AdSizePresetId;
  /** ブランド名 */
  brand?: string;
  /** 商材名 */
  productName?: string;
  /** キャッチコピーやキーメッセージ（任意、AI 生成のヒント） */
  message?: string;
  /** 自由入力トーン（後方互換用、moodKeys と併用可） */
  tone?: string;
  /** デザインムード（複数選択可、バッジ表示） */
  moodKeys?: AdMoodKey[];
  /** 季節感（任意） */
  seasonKey?: AdSeasonKey | null;
  /** キャンペーン情報（セール時期・早期特典等） */
  campaignInfo?: string;
  /** 動画スライド枚数（mediaKind === 'video_slides' のとき） */
  slideCount?: number;

  // ─────────────────────────────────────────────
  // Phase 42: 明示タイプ選択（STEP① ベース画像生成の入力）
  // ─────────────────────────────────────────────
  /**
   * 生成タイプ（明示選択、既定 '03'）。STEP① のベース画像生成方式を決める。
   * - '01' 主題合成型：用意した背景に主題を合成（1 段階）
   * - '02' 主題差し替え型：既存ベース画像の主題だけ差し替え（1 段階）
   * - '03' 背景生成＋合成型：背景を AI 生成して主題を合成（2 段階）
   */
  adType?: '01' | '02' | '03';

  /**
   * 主題画像 URL（タイプ01・03）。広告の主体となる商品・人物の **透明 PNG 推奨**。
   * STEP② で image_placeholder スロットに自動セットされる。背景除去オプション併設。
   */
  subjectImageUrl?: string | null;
  /** 合成指示プロンプト（タイプ01・03）。主題をベース／背景のどこにどう合成するか。 */
  compositePrompt?: string;

  /** 背景画像 URL（タイプ01）。ユーザー供給の背景。 */
  backgroundImageUrl?: string | null;

  /** ベース画像 URL（タイプ02）。主題を差し替える対象の既存広告等。 */
  baseImageUrl?: string | null;
  /** 差し替える主題が写った画像 URL（タイプ02）。ここから新しい主題のみ抽出する。 */
  replacementSubjectUrl?: string | null;
  /** 差し替え合成の指示プロンプト（タイプ02）。 */
  replacePrompt?: string;

  /** 背景生成プロンプト（タイプ03 ステップA）。AI 生成する背景のムード・スタイル指定。 */
  backgroundGenPrompt?: string;
  /** ムードカラー（タイプ03 ステップA・任意）。背景の基調色 #RRGGBB。 */
  moodColor?: string | null;
  /** デザインキー（タイプ03 ステップA・任意）。背景生成のスタイルアンカー語。 */
  designKey?: string | null;

  /**
   * 画像生成方式（タイプ03 選択時のみ表示・適用、既定 'verbal'）。
   * タイプ03 の背景 AI 生成の作り込み度合い／コスト・所要時間のトレードオフ軸。
   * - 'verbal': 軽量。背景を text-to-image で生成し主題をレイヤー合成。低コスト・高速。
   * - 'three_step': 高品質。背景生成 → 主題合成 → 最終仕上げの 3 段階で style 忠実度を高める。
   */
  generationMode?: 'verbal' | 'three_step';

  /**
   * テキストコントラスト方式（任意・上書き用、既定 'auto'）。
   * STEP②／③ が可読性を自動処理するため通常は 'auto' で足りる。手動上書き用に残す。
   * - 'auto': AI 自動（推奨）
   * - 'dark_on_light': 明背景 + 黒系テキスト
   * - 'white_on_dark': 暗背景 + 白系テキスト
   * - 'tinted_overlay': 半透明オーバーレイ + 任意色テキスト
   */
  contrastMode?: 'dark_on_light' | 'white_on_dark' | 'tinted_overlay' | 'auto';
}

/** 配置要素の共通プロパティ（座標は基準解像度 = preset の width/height に対する px） */
export interface AdElementBase {
  id: string;
  /** layer の重なり順序（layers 配列とは別、要素自身が持つ z-index） */
  zIndex: number;
  /** 表示／非表示 */
  visible: boolean;
  /** 座標 */
  x: number;
  y: number;
  w: number;
  h: number;
  /** 回転（度） */
  rotation?: number;
  /** 左右反転（Phase 31） */
  flipX?: boolean;
  /** 上下反転（Phase 31） */
  flipY?: boolean;
  /** ロック */
  locked?: boolean;
  /** Phase 38.4: 所属するグループ ID（slide.groups[].id を参照） */
  groupId?: string;
}

export interface AdTextElement extends AdElementBase {
  kind: 'text';
  content: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  /** 文字色 */
  color: string;
  /** 整列 */
  align: 'left' | 'center' | 'right';
  /** 行間 */
  lineHeight?: number;
  /** 文字間隔 */
  letterSpacing?: number;
  /** 縁取り（影付き表現用） */
  shadow?: { color: string; blur: number; x: number; y: number } | null;
  /** 背景塗り（リボン的な装飾） */
  background?: string | null;
  /** padding（背景塗りがある場合） */
  padding?: number;
}

export interface AdImagePlaceholderElement extends AdElementBase {
  kind: 'image_placeholder';
  /** ユーザーが差し替え可能。null のとき placeholder 表示。 */
  imageUrl: string | null;
  /** 用途ラベル（例: 「商品メイン」「ロゴ」） */
  label: string;
  /** object-fit 風 */
  fit: 'cover' | 'contain' | 'fill';
  /** 角丸 */
  borderRadius?: number;
}

/** ベクター shape の種類（Phase 30 で frame / stamp 追加） */
export type AdVectorShapeId =
  // 基本図形 (1-10)
  | 'rect' | 'rect_rounded' | 'circle' | 'ellipse' | 'triangle'
  | 'diamond' | 'pentagon' | 'hexagon' | 'octagon' | 'star_5'
  // 装飾・記号 (11-20)
  | 'star_6' | 'heart' | 'plus' | 'arrow_right' | 'arrow_up_right'
  | 'chevron_right' | 'check' | 'x_mark' | 'sparkle' | 'lightning'
  // 有機的・抽象 (21-30)
  | 'blob_1' | 'blob_2' | 'blob_3' | 'wave' | 'wave_dense'
  | 'leaf' | 'cloud' | 'speech_bubble' | 'badge_burst' | 'ribbon'
  // Phase 26 追加: タイポ強調・背景パターン (31-34)
  | 'paint_brush' | 'underline_swoosh' | 'grid_overlay' | 'dot_pattern'
  // Phase 30 追加: フレーム (35-42)
  | 'frame_thin_rect' | 'frame_thin_round' | 'frame_double_rect' | 'frame_double_round'
  | 'frame_arch' | 'frame_capsule' | 'frame_corner_brackets' | 'frame_oval_thin'
  // Phase 30 追加: スタンプ装飾 (43-50)
  | 'stamp_sparkle_4pt' | 'stamp_sparkle_8pt' | 'stamp_starburst_12'
  | 'stamp_sunburst_24' | 'stamp_dot_ring' | 'stamp_glow_cluster'
  | 'stamp_diamond_4' | 'stamp_radial_lines';

/** ベクター装飾の役割（AI 配置判断 + UI 表示用） */
export type AdVectorRole =
  | 'decoration'         // 単なる装飾
  | 'background_pattern' // 背景パターン（grid_overlay / dot_pattern など）
  | 'typo_underlay'      // タイポ背後の強調（paint_brush など）
  | 'badge'              // 強調バッジ（badge_burst / ribbon など）
  | 'divider';           // 区切り線

export interface AdVectorElement extends AdElementBase {
  kind: 'vector';
  shape: AdVectorShapeId;
  /** 塗り色（"none" で塗りなし） */
  fill: string;
  /** 線色（"none" で線なし） */
  stroke: string;
  /** 線幅 */
  strokeWidth: number;
  /** 不透明度 (0-1) */
  opacity?: number;
  /** 装飾の役割（任意、AI が役割別 z-index 判断のため） */
  role?: AdVectorRole;
  /**
   * Phase 28.2: 角丸半径 (px)。
   * shape === 'rect_rounded' のときに HTML div + border-radius で描画するため使用。
   * 未指定なら w/h の小さい方の 1/4 を default として使う。
   */
  cornerRadius?: number;
  /**
   * Phase 30: カスタム SVG の Storage URL。
   * 指定された場合は `shape` フィールドを無視し、<img src={customSvgUrl}> で描画する。
   * fill / stroke は SVG ファイル側に焼き込まれた色がそのまま使われる（変更不可）。
   */
  customSvgUrl?: string;
  /** Phase 30: カスタム SVG の表示用ラベル（任意） */
  customSvgLabel?: string;
}

/**
 * Phase 32: ペンツールで描画した自由曲線パス
 *
 * - 頂点（PathPoint）の x/y はエレメントローカル座標（0 が AdElementBase の x/y）
 * - cpIn / cpOut は各頂点からの相対オフセット（イラストレーターの方向ハンドル相当）
 *   - cpIn:  前のセグメントに対する制御点（点に "入る" 方向）
 *   - cpOut: 次のセグメントに対する制御点（点から "出る" 方向）
 * - 両方 0 の頂点は角（コーナー）、ベジェ曲線にしたい場合は値を設定
 * - 描画は SVG <path d="M x y C ... S ... Z?"> を組み立てる
 */
export interface PathPoint {
  x: number;
  y: number;
  cpInX: number;
  cpInY: number;
  cpOutX: number;
  cpOutY: number;
}

export interface AdPathElement extends AdElementBase {
  kind: 'path';
  /** 頂点列（少なくとも 2 点） */
  points: PathPoint[];
  /** パスを閉じるか */
  closed?: boolean;
  /** 塗り色（"none" 可） */
  fill: string;
  /** 線色（"none" 可） */
  stroke: string;
  /** 線幅 (px) */
  strokeWidth: number;
  /** 不透明度 0-1 */
  opacity?: number;
}

export type AdElement = AdTextElement | AdImagePlaceholderElement | AdVectorElement | AdPathElement;

/** 1 スライド分（image なら 1 枚、video_slides なら N 枚） */
export interface AdImageSlide {
  /** 連番 ID */
  id: string;
  /** 背景画像（Gemini 生成） */
  background: {
    url: string;
    prompt: string;
    /** 生成時の seed・モデル ID 等 */
    model?: string;
    /** Storage パス */
    storagePath?: string;
    /**
     * Phase 27.7: 3 段階生成時の中間画像（デバッグ・検証用）。
     * - stepA: ムード画像から被写体を削除した中間画像
     * - stepB: stepA + 主題画像を合成した中間画像
     */
    intermediateImages?: {
      stepA?: string;
      stepB?: string;
    };
    /**
     * Phase 28: 背景画像のレイヤー化対応。
     * 未指定なら全画面 cover（既存動作）。指定すれば部分配置・リサイズ可能。
     * 座標は基準解像度 px。
     */
    x?: number;
    y?: number;
    w?: number;
    h?: number;
    /** object-fit (デフォルト 'cover') */
    fit?: 'cover' | 'contain' | 'fill';
    /** 不透明度 (0-1、デフォルト 1) */
    opacity?: number;
  } | null;
  /** 配置要素 */
  elements: AdElement[];
  /** レイヤー順序（elements の id を並べる、先頭が最前面） */
  layerOrder: string[];
  /** Phase 38.4: グループ定義（fold 表示・名前を保持。実際の所属は element.groupId で参照） */
  groups?: AdGroup[];
  /** 書き出した結果の URL */
  outputs?: {
    png?: string;
    jpg?: string;
  };
}

/** Phase 38.4: レイヤーグループ */
export interface AdGroup {
  id: string;
  name: string;
}

export type AdImageStatus = 'draft' | 'generating' | 'ready' | 'failed';

/** Firestore: adImages/{id} */
export interface AdImage {
  id: string;
  title: string;
  ownerId: string;
  status: AdImageStatus;
  config: AdImageConfig;
  /** 画像 1 枚 or 動画用スライド N 枚 */
  slides: AdImageSlide[];
  /** 関連キャンペーン（任意） */
  sourceCampaignId?: string | null;
  /** バリアント生成元（マスター）の AdImage ID。バリアントでない場合は null */
  parentAdImageId?: string | null;
  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
  /** 生成エラー */
  errorMessage?: string;
}

/** AdImage を新規作成するときの初期値 */
export interface CreateAdImagePayload {
  title?: string;
  config: AdImageConfig;
  sourceCampaignId?: string | null;
}

/** generateAdImage Callable の入力 */
export interface GenerateAdImagePayload {
  adImageId: string;
  /** スライド単位で再生成（mediaKind === 'video_slides' で 1 枚だけ作り直すケース） */
  slideId?: string;
}

/** generateAdImage Callable の戻り値 */
export interface GenerateAdImageResult {
  adImageId: string;
  slides: AdImageSlide[];
}

/** optimizeAdImageText Callable の入力 */
export interface OptimizeAdImageTextPayload {
  adImageId: string;
  slideId: string;
  /** 選択中の要素 ID（任意：指定があればその要素だけ最適化） */
  elementId?: string;
}

export interface OptimizeAdImageTextResult {
  slideId: string;
  elements: AdElement[];
}

/** generateAdImageVariants Callable の入力 */
export interface GenerateAdImageVariantsPayload {
  sourceAdImageId: string;
  /** 展開先サイズ（マスターと同じサイズを含めても無視される） */
  targetSizePresetIds: AdSizePresetId[];
}

export interface GenerateAdImageVariantsResult {
  parentAdImageId: string;
  createdAdImageIds: string[];
  failures: Array<{ sizePresetId: AdSizePresetId; reason: string }>;
}

/** プリセットグループ（バリアント生成ダイアログの「全選択」用） */
export interface AdSizePresetGroup {
  id: 'business_manual' | 'sns_essentials' | 'all_sns';
  label: string;
  description: string;
  sizePresetIds: AdSizePresetId[];
}

export const AD_SIZE_PRESET_GROUPS: readonly AdSizePresetGroup[] = [
  {
    id: 'business_manual',
    label: '業務マニュアル準拠',
    description: '正方形 1200×1200 / 横長 1200×628 / 縦長 960×1200',
    sizePresetIds: ['banner_square_1x1', 'line_card_191x1', 'banner_vertical_4x5'],
  },
  {
    id: 'sns_essentials',
    label: 'SNS 主要 5 サイズ',
    description: 'Facebook 1:1 / Instagram 1:1 / Instagram Stories / YouTube 16:9 / LINE スクエア',
    sizePresetIds: [
      'facebook_feed_1x1',
      'instagram_feed_1x1',
      'instagram_story_9x16',
      'youtube_thumbnail_16x9',
      'line_square_1x1',
    ],
  },
  {
    id: 'all_sns',
    label: '全 SNS（9 サイズ）',
    description: 'すべての SNS プリセットを一括生成',
    sizePresetIds: [
      'facebook_feed_1x1',
      'facebook_feed_4x5',
      'instagram_feed_1x1',
      'instagram_story_9x16',
      'youtube_thumbnail_16x9',
      'youtube_shorts_9x16',
      'line_card_191x1',
      'line_square_1x1',
      'line_vertical_9x16',
    ],
  },
] as const;
